<template>
  <div
    v-if="programmeId"
    id="learner-key-portal-page"
    class="content-with-spacing"
  >
    <div class="heading-container">
      <h1>API Keys</h1>
    </div>

    <section class="keys-stats" aria-labelledby="keys-stats-heading">
      <h2 id="keys-stats-heading" class="sr-only">API key usage</h2>
      <key-portal-page-stats
        label="Keys created this week"
        :value="keysCreatedThisWeek"
        :suffix="`/${maxKeysPerWeek}`"
        :has-warning="keysCreatedThisWeek >= maxKeysPerWeek - 1"
        :warning-message="weeklyKeyWarningMessage"
        :screen-reader-text="`${keysCreatedThisWeek} of ${maxKeysPerWeek} keys created this week`"
        :description="`There is a hard limit of ${maxKeysPerWeek} keys per rolling 7 days.`"
      />
      <key-portal-page-stats
        label="Active key expires in:"
        :value="daysUntilExpiry ?? '—'"
        :suffix="expirySuffix"
        :screen-reader-text="expiryScreenReaderText"
      />
    </section>

    <section aria-labelledby="api-keys-section-heading">
      <k-panel
        :duotone="false"
        :has-content-toggle="false"
      >
        <template #header>
          <h2 id="api-keys-section-heading">API Keys</h2>
        </template>
        <template #body>
          <GenerateKey
            :programme-id="programmeId"
            :disabled="keysCreatedThisWeek >= maxKeysPerWeek"
            @key-created="getApiKeys"
          />

          <hr aria-hidden="true">
          <h3 id="generated-keys-heading">Generated API Keys</h3>
          <generated-keys-table
            :keys="apiKeys"
            labelled-by="generated-keys-heading"
          />
        </template>
      </k-panel>
    </section>
  </div>
</template>

<style scoped>
.keys-stats {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
}

hr {
  margin: 25px 0;
}

h3 {
  margin-top: 0;
  margin-bottom: 25px;
}

:deep(.panel) {
  width: 100%;
}

:deep(.panel-body::before) {
  display: none;
}
</style>

<script>
import { differenceInCalendarDays, isAfter, subDays } from 'date-fns';
import SidebarMixin from '@mixins/sidebar-mixin';
import KPanel from '@base-components/k-panel.vue';
import GenerateKey from './components/generate-key.vue';
import GeneratedKeysTable from './components/generated-keys-table.vue';
import KeyPortalPageStats from './components/key-portal-page-stats.vue';

export default {
  mixins: [SidebarMixin],

  components: {
    KPanel,
    GenerateKey,
    GeneratedKeysTable,
    KeyPortalPageStats,
  },

  props: {
    programmeId: {
      type: Number,
      default: null,
    },
  },

  data() {
    return {
      apiKeys: [],
      maxKeysPerWeek: 3,
    };
  },

  beforeMount() {
    this.$Loading.start();
  },

  watch: {
    programmeId: {
      immediate: true,
      handler(programmeId) {
        if (programmeId) {
          this.getApiKeys();
        }
      },
    },

    sidebarReady(ready) {
      if (ready && !this.programmeId) {
        this.$Loading.finish();
      }
    },
  },

  computed: {
    keysCreatedThisWeek() {
      const cutoff = subDays(new Date(), 7);
      return this.apiKeys.filter(key => isAfter(new Date(key.date_created), cutoff)).length;
    },

    weeklyKeyWarningMessage() {
      if (this.keysCreatedThisWeek >= this.maxKeysPerWeek) {
        return 'You have reached the weekly key limit.';
      }
      return 'You only have 1 key left for this week.';
    },

    daysUntilExpiry() {
      const now = new Date();
      const nextExpiry = this.apiKeys
        .map(key => new Date(key.expire_time))
        .filter(date => isAfter(date, now))
        .sort((a, b) => a - b)[0];

      return nextExpiry ? differenceInCalendarDays(nextExpiry, now) : null;
    },

    expirySuffix() {
      if (this.daysUntilExpiry === null) {
        return '';
      }
      return this.daysUntilExpiry === 1 ? ' day' : ' days';
    },

    expiryScreenReaderText() {
      if (this.daysUntilExpiry === null) {
        return 'No active API key expiry date';
      }
      return `Active key expires in ${this.daysUntilExpiry}${this.expirySuffix}`;
    },
  },

  methods: {
    getApiKeys() {
      this.$logger.info('Getting API Keys', { programmeId: this.programmeId });
      this.$Loading.start();
      this.$http.get(`/api/keymaker/api-keys/programme/${this.programmeId}`)
        .then(result => {
          this.apiKeys = result.data.keys || [];
          this.$logger.info('API Keys fetched successfully', {
            programmeId: this.programmeId,
            keyCount: this.apiKeys.length,
          });
        })
        .catch(error => {
          this.$logger.error('Error getting API Keys', { programmeId: this.programmeId }, error);
          this.$ktoast.showError(error);
        })
        .then(() => {
          this.$Loading.finish();
        });
    },
  },
};
</script>
